// frontend/src/components/admin/ProductForm.js

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import MultiSelect from './MultiSelect';
import fixEncoding from '../../utils/fixEncoding';
import '../../styles/components/ProductForm.scss';

const API_URL = process.env.REACT_APP_API_URL;

const ProductForm = () => {
  const { id } = useParams(); // Para saber si estamos editando (si hay ID) o creando
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  // --- Estados del componente ---
  const [product, setProduct] = useState({
    title: '',
    reference: '',
    description: '',
    price: {},
    variations: {},
    categories: [],
    subcategories: [],
    subcategory_bodega: '',
    locked_categories: false,
    is_deal: false,
  });
  const [allTaxonomies, setAllTaxonomies] = useState([]);
  const [images, setImages] = useState({ existing: [], new: [] });
  const [jsonText, setJsonText] = useState({ price: '{}', variations: '{}' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // --- Cargar datos para el formulario ---
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('admToken');
        const headers = { Authorization: `Bearer ${token}` };

        // 1. Cargar todas las categorías y subcategorías
        const taxResponse = await axios.get(`${API_URL}/admin/products/taxonomies`, { headers });
        setAllTaxonomies(taxResponse.data);

        // 2. Si estamos editando, cargar los datos del producto
        if (isEditing) {
          const prodResponse = await axios.get(`${API_URL}/admin/products/${id}`, { headers });
          const existingProduct = prodResponse.data;
          const price = existingProduct.price || {};
          const variations = existingProduct.variations || {};
          setProduct({
            title: fixEncoding(existingProduct.title || ''),
            reference: existingProduct.reference || '',
            description: fixEncoding(existingProduct.description || ''),
            price,
            variations,
            categories: existingProduct.Categories.map(c => c.id),
            subcategories: existingProduct.Subcategories.map(s => s.id),
            subcategory_bodega: existingProduct.subcategory_bodega || '',
            locked_categories: existingProduct.locked_categories || false,
            is_deal: existingProduct.is_deal || false,
          });
          setJsonText({
            price: JSON.stringify(price, null, 2),
            variations: JSON.stringify(variations, null, 2),
          });
          setImages({ existing: existingProduct.image_files || [], new: [] });
        }
      } catch (err) {
        setError('Error al cargar los datos. ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, isEditing]);

  // --- Manejadores de eventos del formulario ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProduct(prev => ({ ...prev, [name]: value }));
  };

  const handleJsonChange = (e, field) => {
    const text = e.target.value;
    setJsonText(prev => ({ ...prev, [field]: text }));
    try {
      const parsedJson = JSON.parse(text);
      setProduct(prev => ({ ...prev, [field]: parsedJson }));
      setError('');
    } catch {
      // keep last valid parsed value; error shown on blur
    }
  };

  const handleJsonBlur = (field) => {
    try {
      JSON.parse(jsonText[field]);
      setError('');
    } catch {
      setError(`El formato del JSON en el campo '${field}' es inválido.`);
    }
  };


  const handleImageChange = (e) => {
    setImages(prev => ({ ...prev, new: [...e.target.files] }));
  };
  
  const removeExistingImage = (imageToRemove) => {
    setImages(prev => ({ ...prev, existing: prev.existing.filter(img => img !== imageToRemove)}));
  };

  // --- Lógica de envío del formulario ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    for (const field of ['price', 'variations']) {
      try { JSON.parse(jsonText[field]); } catch {
        setError(`El formato del JSON en el campo '${field}' es inválido.`);
        return;
      }
    }

    const formData = new FormData();
    // Añadir campos de texto
    formData.append('title', product.title);
    formData.append('reference', product.reference);
    formData.append('description', product.description);

    // Añadir campos JSON como strings
    formData.append('price', JSON.stringify(product.price));
    formData.append('variations', JSON.stringify(product.variations));
    
    // Añadir taxonomías y bloqueo
    formData.append('categories', JSON.stringify(product.categories));
    formData.append('subcategories', JSON.stringify(product.subcategories));
    // subcategoryBodegaSamples es de solo lectura (lo gestiona el sync)
    formData.append('locked_categories', product.locked_categories);
    // is_deal: pendiente hasta que el backend soporte el campo
    
    // Añadir imágenes
    images.new.forEach(file => formData.append('images', file));
    if (isEditing) {
      formData.append('existingImages', JSON.stringify(images.existing));
    }

    try {
      const token = localStorage.getItem('admToken');
      const config = { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }};

      if (isEditing) {
        await axios.put(`${API_URL}/admin/products/${id}`, formData, config);
        alert('Producto actualizado exitosamente!');
      } else {
        await axios.post(`${API_URL}/admin/products`, formData, config);
        alert('Producto creado exitosamente!');
      }
      navigate(-1);
    } catch (err) {
      setError('Error al guardar el producto. ' + (err.response?.data?.message || err.message));
    }
  };
  
  // --- Renderizado ---
  if (loading) return <div>Cargando formulario...</div>;

  return (
    <form className="product-form" onSubmit={handleSubmit}>
      <h2>{isEditing ? 'Editar Producto' : 'Crear Nuevo Producto'}</h2>
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="form-group">
        <label>Título</label>
        <input type="text" name="title" value={product.title} onChange={handleInputChange} required />
      </div>

      <div className="form-group">
        <label>Referencia</label>
        <input type="text" name="reference" value={product.reference} onChange={handleInputChange} required />
      </div>

      <div className="form-group">
        <label>Descripción</label>
        <textarea name="description" value={product.description} onChange={handleInputChange}></textarea>
      </div>

      <div className="form-group">
        <label>Precio (JSON)</label>
        <textarea value={jsonText.price} onChange={(e) => handleJsonChange(e, 'price')} onBlur={() => handleJsonBlur('price')}></textarea>
      </div>

      <div className="form-group">
        <label>Variaciones (JSON)</label>
        <textarea value={jsonText.variations} onChange={(e) => handleJsonChange(e, 'variations')} onBlur={() => handleJsonBlur('variations')}></textarea>
      </div>

      <div className="form-group">
        <label>Categorías</label>
        <MultiSelect
          placeholder="Seleccionar categorías..."
          options={allTaxonomies.map(cat => ({ value: cat.id, label: cat.name }))}
          value={product.categories}
          onChange={(ids) => setProduct(prev => {
            // Al cambiar las categorías, se descartan las subcategorías que ya no
            // pertenezcan a ninguna categoría seleccionada.
            const allowedSubIds = new Set(
              allTaxonomies
                .filter(cat => ids.includes(cat.id))
                .flatMap(cat => cat.subcategories.map(sub => sub.id))
            );
            return {
              ...prev,
              categories: ids,
              subcategories: prev.subcategories.filter(sid => allowedSubIds.has(sid)),
            };
          })}
        />
      </div>

      <div className="form-group">
        <label>Subcategoría de bodega</label>
        <div className="form-readonly">
          {product.subcategory_bodega
            ? fixEncoding(product.subcategory_bodega)
            : <span className="form-readonly__empty">Sin datos (se llena con el sync)</span>
          }
        </div>
        <small className="form-hint">Subcategoría del proveedor — gestionada automáticamente por el sync.</small>
      </div>

      <div className="form-group">
        <label>Subcategorías (en stock)</label>
        <MultiSelect
          placeholder={
            product.categories.length === 0
              ? 'Selecciona una categoría primero...'
              : 'Seleccionar subcategorías...'
          }
          options={allTaxonomies
            .filter(cat => product.categories.includes(cat.id))
            .flatMap(cat => [
              { value: `group-${cat.id}`, label: cat.name, isGroup: true },
              ...cat.subcategories.map(sub => ({ value: sub.id, label: sub.name })),
            ])}
          value={product.subcategories}
          onChange={(ids) => setProduct(prev => ({ ...prev, subcategories: ids }))}
        />
        <small className="form-hint">
          Solo se muestran las subcategorías de las categorías seleccionadas arriba.
        </small>
      </div>
      
      {/* ── Bloqueo de categorías ────────────────────────── */}
      <div className={`lock-categories-box${product.locked_categories ? ' lock-categories-box--on' : ''}`}>
        <div className="lock-categories-box__info">
          <span className="lock-categories-box__icon">{product.locked_categories ? '🔒' : '🔓'}</span>
          <div>
            <p className="lock-categories-box__title">Categorías bloqueadas para sync</p>
            <p className="lock-categories-box__desc">
              {product.locked_categories
                ? 'El sync respetará la categoría asignada manualmente. No se sobreescribirá.'
                : 'El sync puede reasignar la categoría según el mapeo de bodegas.'}
            </p>
          </div>
        </div>
        <label className="lock-toggle">
          <input
            type="checkbox"
            checked={product.locked_categories}
            onChange={(e) => setProduct(prev => ({ ...prev, locked_categories: e.target.checked }))}
          />
          <span className="lock-toggle__track">
            <span className="lock-toggle__thumb" />
          </span>
        </label>
      </div>

      {/* ── Marcar como oferta ───────────────────────────── */}
      <div className={`lock-categories-box${product.is_deal ? ' lock-categories-box--deal' : ''}`}>
        <div className="lock-categories-box__info">
          <span className="lock-categories-box__icon">{product.is_deal ? '🔥' : '🏷️'}</span>
          <div>
            <p className="lock-categories-box__title">Oferta especial</p>
            <p className="lock-categories-box__desc">
              {product.is_deal
                ? 'Este producto aparece en la sección de Ofertas en el home.'
                : 'Activa para que aparezca en la sección de Ofertas del home.'}
            </p>
          </div>
        </div>
        <label className="lock-toggle">
          <input
            type="checkbox"
            checked={product.is_deal}
            onChange={(e) => setProduct(prev => ({ ...prev, is_deal: e.target.checked }))}
          />
          <span className="lock-toggle__track">
            <span className="lock-toggle__thumb" />
          </span>
        </label>
      </div>

      <div className="form-group">
        <label>Imágenes</label>
        {isEditing && images.existing.length > 0 && (
          <div className="image-preview">
            <p>Imágenes actuales:</p>
            {images.existing.map(img => (
              <div key={img} className="image-item">
                <img src={`${process.env.REACT_APP_S3_BASE_URL}/thumbnails_mini/${img}`} alt="thumbnail" />
                <span>{img.substring(0, 20)}...</span>
                <button type="button" onClick={() => removeExistingImage(img)}>×</button>
              </div>
            ))}
          </div>
        )}
        <input type="file" multiple onChange={handleImageChange} accept="image/*" />
      </div>

      <div className="form-actions">
        <button type="submit" className="btn btn-primary">
          {isEditing ? 'Actualizar Producto' : 'Guardar Producto'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>
          Cancelar
        </button>
      </div>
      
    </form>
  );
};

export default ProductForm;